import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Plus, Filter, X, Users, Loader2, ChevronDown, ChevronUp,
  Pencil, Trash2, Upload, Building2, ScanBarcode,
} from 'lucide-react';
import { api } from '@/utils/api';
import { formatDate } from '@/utils/formatters';

interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  title?: string;
  source?: string;
  leadScore?: number;
  status?: string;
  productInterest?: string;
  city?: string;
  state?: string;
  notes?: string;
  customerType?: string;
  createdAt: string;
  account?: { id: string; name: string } | null;
}

interface Account {
  id: string;
  name: string;
}

interface Product {
  id: string;
  name: string;
}

const SOURCES = ['Website', 'Referral', 'Social Media', 'Cold Call', 'Email Campaign', 'Trade Show', 'Partner', 'Other'];
const STATUSES = ['lead', 'prospect', 'customer', 'inactive'];

export function ContactsPage() {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;
  const totalPages = Math.ceil(total / limit);

  // Filters
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [filterCustomerType, setFilterCustomerType] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Sort
  const [sortField, setSortField] = useState<string>('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  // Create Modal
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', phone: '', title: '',
    accountId: '', source: '', tags: '',
    productInterest: '', notes: '', customerType: 'b2c' as string,
    street: '', city: '', state: '', pincode: '', gstin: '',
  });

  // GST lookup state for ContactsPage
  const [gstFetching, setGstFetching] = useState(false);
  const [gstFetched, setGstFetched] = useState(false);
  const [gstCompanyName, setGstCompanyName] = useState('');
  const [gstEnrichment, setGstEnrichment] = useState<any>(null);

  // GST lookup for contacts
  const handleGstLookup = async (gstin: string) => {
    if (gstin.length !== 15) return;
    setGstFetching(true);
    try {
      const res = await api.get(`/accounts/gst-lookup/${gstin}`);
      const d = res.data.data;
      if (d.companyName || d.tradeName || d.legalName) {
        setGstCompanyName(d.companyName || d.tradeName || d.legalName);
        setGstFetched(true);
        setGstEnrichment({
          tradeName: d.tradeName || null,
          legalName: d.legalName || null,
          gstStatus: d.gstStatus || null,
          gstRegistrationDate: d.registrationDate || null,
          gstBusinessType: d.businessType || null,
          gstLastUpdated: d.lastUpdated || null,
          gstRawData: d.rawData || null,
          stateCode: d.stateCode || null,
          pan: d.pan || null,
          gstAddress: d.address || null,
        });
        // Auto-fill state from GST
        if (d.stateName && !form.state) {
          setForm(f => ({ ...f, state: d.stateName }));
        }
      }
    } catch { /* ignore — offline fallback will be used */ }
    setGstFetching(false);
  };

  // Edit Modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({
    firstName: '', lastName: '', email: '', phone: '', title: '',
    accountId: '', source: '', tags: '',
    productInterest: '', notes: '', customerType: 'b2c' as string,
    street: '', city: '', state: '', pincode: '',
  });
  const [editSaving, setEditSaving] = useState(false);

  // Delete confirmation
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteName, setDeleteName] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Import CSV
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importPreview, setImportPreview] = useState<Record<string, string>[]>([]);
  const [importAllRows, setImportAllRows] = useState<Record<string, string>[]>([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ success: number; errors: number } | null>(null);

  // Summary stats
  const summaryStats = useMemo(() => {
    // BUG-001 fix: use pagination total from API, not current page size
    const pageSize = contacts.length;
    const now = new Date();
    const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const newThisMonth = contacts.filter(c => new Date(c.createdAt) >= firstOfMonth).length;

    // Source breakdown
    const sourceCounts: Record<string, number> = {};
    contacts.forEach(c => {
      const src = c.source || 'Unknown';
      sourceCounts[src] = (sourceCounts[src] || 0) + 1;
    });
    const topSources = Object.entries(sourceCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3);

    const avgLeadScore = pageSize > 0
      ? Math.round(contacts.reduce((sum, c) => sum + (c.leadScore ?? 0), 0) / pageSize)
      : 0;

    return { total, newThisMonth, topSources, avgLeadScore };
  }, [contacts, total]);

  const fetchContacts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params: Record<string, string> = {
        page: String(page),
        limit: String(limit),
      };
      if (search) params.search = search;
      if (filterStatus) params.status = filterStatus;
      if (filterSource) params.source = filterSource;
      if (filterCustomerType) params.customerType = filterCustomerType;
      if (sortField) params.sortBy = sortField;
      if (sortDir) params.sortOrder = sortDir;
      const res = await api.get('/contacts', { params });
      setContacts(res.data.data?.contacts ?? res.data.data ?? []);
      setTotal(res.data.data?.pagination?.total ?? res.data.pagination?.total ?? res.data.total ?? 0);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load contacts');
    } finally {
      setLoading(false);
    }
  }, [search, filterStatus, filterSource, filterCustomerType, sortField, sortDir, page]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  useEffect(() => {
    api.get('/accounts').then(res => {
      setAccounts(res.data.data?.accounts ?? res.data.data ?? []);
    }).catch(() => {});
    api.get('/products').then(res => {
      setProducts(res.data.data?.products ?? res.data.data ?? []);
    }).catch(() => {});
  }, []);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  };

  const handleCreate = async () => {
    try {
      setSaving(true);
      const { street, city, state, pincode, productInterest, notes, gstin, ...rest } = form;
      const address = (street || city || state || pincode)
        ? { street, city, state, pincode }
        : undefined;
      await api.post('/contacts', {
        ...rest,
        accountId: form.accountId || undefined,
        tags: form.tags ? form.tags.split(',').map(t => t.trim()) : [],
        address,
        productInterest: productInterest || undefined,
        city: city || undefined,
        state: state || undefined,
        notes: notes || undefined,
        gstin: gstin || undefined,
        // Pass company name + enrichment so backend can auto-create Account
        companyName: gstCompanyName || undefined,
        ...(gstEnrichment || {}),
      });
      setShowModal(false);
      setForm({ firstName: '', lastName: '', email: '', phone: '', title: '', accountId: '', source: '', tags: '', productInterest: '', notes: '', customerType: 'b2c', street: '', city: '', state: '', pincode: '', gstin: '' });
      setGstFetched(false); setGstCompanyName(''); setGstEnrichment(null);
      fetchContacts();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to create contact');
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (c: Contact, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditId(c.id);
    const addr = (c as any).address as { street?: string; city?: string; state?: string; pincode?: string } | null;
    setEditForm({
      firstName: c.firstName, lastName: c.lastName, email: c.email || '',
      phone: c.phone || '', title: c.title || '',
      accountId: c.account?.id || '', source: c.source || '', tags: '',
      productInterest: c.productInterest || '', notes: c.notes || '', customerType: (c as any).customerType || 'b2c',
      street: addr?.street || '', city: addr?.city || c.city || '', state: addr?.state || c.state || '', pincode: addr?.pincode || '',
    });
    setShowEditModal(true);
  };

  const handleEdit = async () => {
    if (!editId) return;
    try {
      setEditSaving(true);
      const { street, city, state, pincode, productInterest, notes, ...editRest } = editForm;
      const editAddress = (street || city || state || pincode)
        ? { street, city, state, pincode }
        : undefined;
      await api.put(`/contacts/${editId}`, {
        ...editRest,
        accountId: editForm.accountId || undefined,
        tags: editForm.tags ? editForm.tags.split(',').map(t => t.trim()) : undefined,
        address: editAddress,
        productInterest: productInterest || undefined,
        city: city || undefined,
        state: state || undefined,
        notes: notes || undefined,
      });
      setShowEditModal(false);
      setEditId(null);
      fetchContacts();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to update contact');
    } finally {
      setEditSaving(false);
    }
  };

  const openDelete = (c: Contact, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteId(c.id);
    setDeleteName(`${c.firstName} ${c.lastName}`);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      setDeleting(true);
      await api.delete(`/contacts/${deleteId}`);
      setDeleteId(null);
      fetchContacts();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to delete contact');
    } finally {
      setDeleting(false);
    }
  };

  const handleCsvFileChange = (file: File | null) => {
    setImportFile(file);
    setImportResult(null);
    if (!file) { setImportPreview([]); setImportAllRows([]); return; }
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
      if (lines.length < 2) { setImportPreview([]); setImportAllRows([]); return; }
      const headers = lines[0].split(',').map(h => h.trim().replace(/^["']|["']$/g, ''));
      const rows = lines.slice(1).map(line => {
        const vals = line.split(',').map(v => v.trim().replace(/^["']|["']$/g, ''));
        const row: Record<string, string> = {};
        headers.forEach((h, i) => { row[h] = vals[i] || ''; });
        return row;
      });
      setImportAllRows(rows);
      setImportPreview(rows.slice(0, 5));
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (importAllRows.length === 0) return;
    setImporting(true);
    setImportResult(null);
    try {
      const FIELD_MAP: Record<string, string> = {
        firstname: 'firstName', first_name: 'firstName', 'first name': 'firstName',
        lastname: 'lastName', last_name: 'lastName', 'last name': 'lastName',
        email: 'email', phone: 'phone', title: 'title', source: 'source',
      };
      const mapped = importAllRows.map(row => {
        const contact: Record<string, string> = {};
        Object.entries(row).forEach(([key, val]) => {
          const mapped_key = FIELD_MAP[key.toLowerCase()] || key;
          contact[mapped_key] = val;
        });
        return contact;
      }).filter(c => c.firstName && c.lastName);

      const res = await api.post('/contacts/import', { contacts: mapped });
      const count = res.data.data?.count ?? mapped.length;
      setImportResult({ success: count, errors: importAllRows.length - mapped.length });
      fetchContacts();
    } catch (err: any) {
      setImportResult({ success: 0, errors: importAllRows.length });
      alert(err.response?.data?.error || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div>
      {/* Header — Apr 23, 2026 (Agent 4 / Fix B): grid with auto-column for the
          action buttons + truncate on the title so the buttons are ALWAYS on
          screen regardless of viewport width or sidebar/AI panel state.
          `flex-wrap` alone was insufficient at 1512px — the long subtitle +
          title column was still eating enough horizontal space to clip the
          Add Contact button. Grid with min-w-0 on the title column forces the
          title to truncate before the buttons overflow. */}
      <div className="mb-6 grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3">
        <div className="min-w-0">
          <h1 className="truncate text-2xl font-bold text-gray-900">Contacts</h1>
          <p className="truncate text-sm text-gray-500">Manage your contacts and leads</p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
          <button
            onClick={() => { setShowImportModal(true); setImportFile(null); setImportPreview([]); setImportAllRows([]); setImportResult(null); }}
            className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <Upload size={16} />
            Import CSV
          </button>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] transition-colors"
          >
            <Plus size={16} />
            Add Contact
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-xs text-gray-500">Total Contacts</p>
          <p className="mt-1 text-lg font-bold text-gray-900">{summaryStats.total}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-xs text-gray-500">New This Month</p>
          <p className="mt-1 text-lg font-bold text-[var(--ink-0)]">{summaryStats.newThisMonth}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-xs text-gray-500">Top Sources</p>
          <div className="mt-1">
            {summaryStats.topSources.length > 0 ? summaryStats.topSources.map(([src, count]) => (
              <span key={src} className="mr-2 inline-flex items-center text-xs text-gray-700">
                {src} <span className="ml-1 font-medium text-[var(--ink-0)]">({count})</span>
              </span>
            )) : <span className="text-xs text-gray-400">No data</span>}
          </div>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-xs text-gray-500">Avg Lead Score</p>
          <p className="mt-1 text-lg font-bold text-gray-900">{summaryStats.avgLeadScore}</p>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search contacts..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-4 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
          />
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${showFilters ? 'border-[var(--ink-0)] bg-[var(--saffron-50)] text-[var(--ink-0)]' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`}
        >
          <Filter size={16} />
          Filters
        </button>
      </div>

      {showFilters && (
        <div className="mb-4 flex flex-wrap gap-3 rounded-lg border border-gray-200 bg-white p-4">
          <select
            value={filterStatus}
            onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
          >
            <option value="">All Statuses</option>
            {STATUSES.map(s => (
              <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
            ))}
          </select>
          <select
            value={filterSource}
            onChange={(e) => { setFilterSource(e.target.value); setPage(1); }}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
          >
            <option value="">All Sources</option>
            {SOURCES.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <select
            value={filterCustomerType}
            onChange={(e) => { setFilterCustomerType(e.target.value); setPage(1); }}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
          >
            <option value="">All Types</option>
            <option value="b2b">B2B</option>
            <option value="b2c">B2C</option>
          </select>
          {(filterStatus || filterSource || filterCustomerType) && (
            <button
              onClick={() => { setFilterStatus(''); setFilterSource(''); setFilterCustomerType(''); setPage(1); }}
              className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
            >
              <X size={14} />
              Clear
            </button>
          )}
        </div>
      )}

      {/* Table */}
      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
            <span className="ml-2 text-sm text-gray-500">Loading contacts...</span>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-20 text-red-500">
            <p className="text-sm">{error}</p>
            <button onClick={fetchContacts} className="mt-2 text-sm text-[var(--ink-0)] hover:underline">Retry</button>
          </div>
        ) : contacts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <Users size={40} className="mb-2" />
            <p className="text-sm">No contacts found</p>
            <p className="text-xs">Add your first contact to get started</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  <th className="cursor-pointer px-4 py-3" onClick={() => handleSort('firstName')}>
                    <span className="flex items-center gap-1">Name <SortIcon field="firstName" /></span>
                  </th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Phone</th>
                  <th className="px-4 py-3">Product Interest</th>
                  <th className="px-4 py-3">City</th>
                  <th className="px-4 py-3">Account</th>
                  <th className="cursor-pointer px-4 py-3" onClick={() => handleSort('source')}>
                    <span className="flex items-center gap-1">Source <SortIcon field="source" /></span>
                  </th>
                  <th className="cursor-pointer px-4 py-3" onClick={() => handleSort('leadScore')}>
                    <span className="flex items-center gap-1">Lead Score <SortIcon field="leadScore" /></span>
                  </th>
                  <th className="cursor-pointer px-4 py-3" onClick={() => handleSort('createdAt')}>
                    <span className="flex items-center gap-1">Created <SortIcon field="createdAt" /></span>
                  </th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {contacts.map((c) => (
                  <tr
                    key={c.id}
                    onClick={() => navigate(`/contacts/${c.id}`)}
                    className="cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-4 py-3 font-medium text-gray-900">
                      <span className="flex items-center gap-2">
                        {c.firstName} {c.lastName}
                        <span className={`inline-flex rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${c.customerType === 'b2b' ? 'bg-indigo-50 text-indigo-700' : 'bg-gray-100 text-gray-500'}`}>
                          {c.customerType === 'b2b' ? 'B2B' : 'B2C'}
                        </span>
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{c.email}</td>
                    {/* BUG-C03 fix (Wave-3 May 1 2026): phone as tel: link */}
                    <td className="px-4 py-3 text-gray-600">
                      {c.phone
                        ? <a href={`tel:${c.phone.replace(/\s+/g, '')}`} className="hover:underline">{c.phone}</a>
                        : '-'}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {c.productInterest ? (
                        <span className="inline-flex rounded-full bg-purple-50 px-2 py-0.5 text-xs font-medium text-purple-700">
                          {c.productInterest}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{c.city || '-'}</td>
                    <td className="px-4 py-3 text-gray-600">{c.account?.name || '-'}</td>
                    <td className="px-4 py-3">
                      {c.source ? (
                        <span className="inline-flex rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                          {c.source}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="px-4 py-3">
                      {c.leadScore != null ? (
                        <div className="flex items-center gap-2">
                          <div className="h-2 w-16 rounded-full bg-gray-200">
                            <div
                              className="h-2 rounded-full bg-[var(--ink-0)]"
                              style={{ width: `${Math.min(c.leadScore, 100)}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-600">{c.leadScore}</span>
                        </div>
                      ) : '-'}
                    </td>
                    <td className="px-4 py-3 text-gray-500">{formatDate(c.createdAt)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={(e) => openEdit(c, e)}
                          className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-[var(--ink-0)] transition-colors"
                          title="Edit contact"
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          onClick={(e) => openDelete(c, e)}
                          className="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                          title="Delete contact"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
            <div className="text-sm text-gray-500">
              Showing {((page - 1) * limit) + 1}–{Math.min(page * limit, total)} of {total}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 text-sm rounded border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-sm text-gray-500">Page {page} of {totalPages}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1 text-sm rounded border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create Contact Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowModal(false)}>
          <div className="mx-4 w-full max-w-lg rounded-xl bg-white shadow-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white px-6 py-4 rounded-t-xl">
              <h2 className="text-lg font-semibold text-gray-900">Add Contact</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4 px-6 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">First Name *</label>
                  <input
                    type="text"
                    value={form.firstName}
                    onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Last Name *</label>
                  <input
                    type="text"
                    value={form.lastName}
                    onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                  />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Email *</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Phone</label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Title</label>
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                  />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Product Interest</label>
                <div className="relative">
                  <div
                    className="min-h-[38px] w-full cursor-pointer rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus-within:border-[var(--ink-0)] focus-within:ring-1 focus-within:ring-[var(--ink-0)]"
                    onClick={() => {
                      const el = document.getElementById('pi-dropdown-create');
                      if (el) el.classList.toggle('hidden');
                    }}
                  >
                    {form.productInterest ? (
                      <div className="flex flex-wrap gap-1">
                        {form.productInterest.split(', ').map((p, i) => (
                          <span key={i} className="inline-flex items-center gap-1 rounded-full bg-purple-50 px-2 py-0.5 text-xs font-medium text-purple-700">
                            {p}
                            <button type="button" onClick={(e) => {
                              e.stopPropagation();
                              const updated = form.productInterest.split(', ').filter((_, idx) => idx !== i).join(', ');
                              setForm({ ...form, productInterest: updated });
                            }} className="text-purple-400 hover:text-purple-600"><X size={10} /></button>
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-400">Select products...</span>
                    )}
                  </div>
                  <div id="pi-dropdown-create" className="hidden absolute z-20 mt-1 w-full max-h-48 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                    {products.map(p => {
                      const selected = form.productInterest.split(', ').filter(Boolean).includes(p.name);
                      return (
                        <label key={p.id} className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => {
                              const current = form.productInterest.split(', ').filter(Boolean);
                              const updated = selected ? current.filter(x => x !== p.name) : [...current, p.name];
                              setForm({ ...form, productInterest: updated.join(', ') });
                            }}
                            className="rounded border-gray-300 text-[var(--ink-0)] focus:ring-[var(--ink-0)]"
                          />
                          {p.name}
                        </label>
                      );
                    })}
                    {products.length === 0 && <div className="px-3 py-2 text-xs text-gray-400">No products found</div>}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Account</label>
                  <select
                    value={form.accountId}
                    onChange={(e) => setForm({ ...form, accountId: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                  >
                    <option value="">None</option>
                    {accounts.map(a => (
                      <option key={a.id} value={a.id}>{a.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Source</label>
                  <select
                    value={form.source}
                    onChange={(e) => setForm({ ...form, source: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                  >
                    <option value="">Select Source</option>
                    {SOURCES.map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Customer Type</label>
                  <select
                    value={form.customerType}
                    onChange={(e) => {
                      setForm({ ...form, customerType: e.target.value });
                      if (e.target.value === 'b2c') {
                        setGstFetched(false); setGstCompanyName(''); setGstEnrichment(null);
                        setForm(f => ({ ...f, customerType: 'b2c', gstin: '' }));
                      }
                    }}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                  >
                    <option value="b2c">B2C — Individual Consumer</option>
                    <option value="b2b">B2B — Business Buyer</option>
                  </select>
                </div>
              </div>
              {/* GST Lookup — shown when B2B */}
              {form.customerType === 'b2b' && (
                <div className="rounded-lg border border-blue-100 bg-blue-50/50 p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Building2 size={14} className="text-blue-600" />
                    <span className="text-xs font-semibold text-blue-700 uppercase tracking-wider">GST Details</span>
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="text"
                      placeholder="Enter 15-digit GSTIN"
                      value={form.gstin}
                      onChange={(e) => {
                        const v = e.target.value.toUpperCase().slice(0, 15);
                        setForm({ ...form, gstin: v });
                        if (v.length === 15) handleGstLookup(v);
                      }}
                      maxLength={15}
                      className={`flex-1 rounded-lg border px-3 py-2 text-sm font-mono focus:border-[var(--ink-0)] focus:outline-none ${gstFetched ? 'border-green-300 bg-green-50' : 'border-gray-300'}`}
                    />
                    <button
                      type="button"
                      onClick={() => handleGstLookup(form.gstin)}
                      disabled={form.gstin.length !== 15 || gstFetching}
                      className="flex items-center gap-1 rounded-lg bg-blue-600 px-3 py-2 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                    >
                      {gstFetching ? <Loader2 size={12} className="animate-spin" /> : <ScanBarcode size={12} />}
                      Lookup
                    </button>
                  </div>
                  {gstFetched && gstCompanyName && (
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-green-600 font-medium">✓ {gstCompanyName}</span>
                      <span className="text-xs text-gray-500">(Account will be auto-created)</span>
                    </div>
                  )}
                  {!gstFetched && !gstFetching && form.gstin.length > 0 && form.gstin.length < 15 && (
                    <p className="text-xs text-gray-400">{15 - form.gstin.length} more characters needed</p>
                  )}
                </div>
              )}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Tags</label>
                <input
                  type="text"
                  placeholder="Comma-separated tags"
                  value={form.tags}
                  onChange={(e) => setForm({ ...form, tags: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                />
              </div>
              <div className="border-t border-gray-100 pt-3">
                <p className="mb-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Location</p>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <input type="text" placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                  <input type="text" placeholder="State" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Notes</label>
                <textarea
                  rows={3}
                  placeholder="Free-form notes about this contact..."
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                />
              </div>
              <div className="border-t border-gray-100 pt-3">
                <p className="mb-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Address</p>
                <div className="space-y-3">
                  <input type="text" placeholder="Street" value={form.street} onChange={(e) => setForm({ ...form, street: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                  <div className="grid grid-cols-3 gap-3">
                    <input type="text" placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                    <input type="text" placeholder="State" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                    <input type="text" placeholder="Pincode" value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <button
                onClick={() => setShowModal(false)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={saving || !form.firstName || !form.lastName || !form.email}
                className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
              >
                {saving && <Loader2 size={14} className="animate-spin" />}
                Create Contact
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Contact Modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => { setShowEditModal(false); setEditId(null); }}>
          <div className="mx-4 w-full max-w-lg rounded-xl bg-white shadow-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white px-6 py-4 rounded-t-xl">
              <h2 className="text-lg font-semibold text-gray-900">Edit Contact</h2>
              <button onClick={() => { setShowEditModal(false); setEditId(null); }} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4 px-6 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">First Name *</label>
                  <input type="text" value={editForm.firstName} onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Last Name *</label>
                  <input type="text" value={editForm.lastName} onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Email</label>
                <input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Phone</label>
                  <input type="tel" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Title</label>
                  <input type="text" value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Product Interest</label>
                <div className="relative">
                  <div
                    className="min-h-[38px] w-full cursor-pointer rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus-within:border-[var(--ink-0)] focus-within:ring-1 focus-within:ring-[var(--ink-0)]"
                    onClick={() => {
                      const el = document.getElementById('pi-dropdown-edit');
                      if (el) el.classList.toggle('hidden');
                    }}
                  >
                    {editForm.productInterest ? (
                      <div className="flex flex-wrap gap-1">
                        {editForm.productInterest.split(', ').map((p, i) => (
                          <span key={i} className="inline-flex items-center gap-1 rounded-full bg-purple-50 px-2 py-0.5 text-xs font-medium text-purple-700">
                            {p}
                            <button type="button" onClick={(e) => {
                              e.stopPropagation();
                              const updated = editForm.productInterest.split(', ').filter((_, idx) => idx !== i).join(', ');
                              setEditForm({ ...editForm, productInterest: updated });
                            }} className="text-purple-400 hover:text-purple-600"><X size={10} /></button>
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-400">Select products...</span>
                    )}
                  </div>
                  <div id="pi-dropdown-edit" className="hidden absolute z-20 mt-1 w-full max-h-48 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                    {products.map(p => {
                      const selected = editForm.productInterest.split(', ').filter(Boolean).includes(p.name);
                      return (
                        <label key={p.id} className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => {
                              const current = editForm.productInterest.split(', ').filter(Boolean);
                              const updated = selected ? current.filter(x => x !== p.name) : [...current, p.name];
                              setEditForm({ ...editForm, productInterest: updated.join(', ') });
                            }}
                            className="rounded border-gray-300 text-[var(--ink-0)] focus:ring-[var(--ink-0)]"
                          />
                          {p.name}
                        </label>
                      );
                    })}
                    {products.length === 0 && <div className="px-3 py-2 text-xs text-gray-400">No products found</div>}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Account</label>
                  <select value={editForm.accountId} onChange={(e) => setEditForm({ ...editForm, accountId: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none">
                    <option value="">None</option>
                    {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Source</label>
                  <select value={editForm.source} onChange={(e) => setEditForm({ ...editForm, source: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none">
                    <option value="">Select Source</option>
                    {SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Tags</label>
                <input type="text" placeholder="Comma-separated tags" value={editForm.tags} onChange={(e) => setEditForm({ ...editForm, tags: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
              </div>
              <div className="border-t border-gray-100 pt-3">
                <p className="mb-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Location</p>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <input type="text" placeholder="City" value={editForm.city} onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                  <input type="text" placeholder="State" value={editForm.state} onChange={(e) => setEditForm({ ...editForm, state: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Notes</label>
                <textarea rows={3} placeholder="Free-form notes about this contact..." value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
              </div>
              <div className="border-t border-gray-100 pt-3">
                <p className="mb-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Address</p>
                <div className="space-y-3">
                  <input type="text" placeholder="Street" value={editForm.street} onChange={(e) => setEditForm({ ...editForm, street: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                  <div className="grid grid-cols-3 gap-3">
                    <input type="text" placeholder="City" value={editForm.city} onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                    <input type="text" placeholder="State" value={editForm.state} onChange={(e) => setEditForm({ ...editForm, state: e.target.value })}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                    <input type="text" placeholder="Pincode" value={editForm.pincode} onChange={(e) => setEditForm({ ...editForm, pincode: e.target.value })}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <button onClick={() => { setShowEditModal(false); setEditId(null); }}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={handleEdit} disabled={editSaving || !editForm.firstName || !editForm.lastName}
                className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50">
                {editSaving && <Loader2 size={14} className="animate-spin" />}
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setDeleteId(null)}>
          <div className="mx-4 w-full max-w-sm rounded-xl bg-white shadow-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-5 sticky top-0 z-10 bg-white rounded-t-xl">
              <h2 className="mb-2 text-lg font-semibold text-gray-900">Delete Contact</h2>
              <p className="text-sm text-gray-600">
                Are you sure you want to delete <span className="font-medium">{deleteName}</span>? This action cannot be undone.
              </p>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <button onClick={() => setDeleteId(null)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={handleDelete} disabled={deleting}
                className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50">
                {deleting && <Loader2 size={14} className="animate-spin" />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import CSV Modal */}
      {showImportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowImportModal(false)}>
          <div className="mx-4 w-full max-w-2xl rounded-xl bg-white shadow-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 sticky top-0 z-10 bg-white rounded-t-xl">
              <h2 className="text-lg font-semibold text-gray-900">Import Contacts from CSV</h2>
              <button onClick={() => setShowImportModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4 px-6 py-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Upload CSV File</label>
                <input
                  type="file"
                  accept=".csv"
                  onChange={(e) => handleCsvFileChange(e.target.files?.[0] || null)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm file:mr-3 file:rounded-md file:border-0 file:bg-[var(--saffron-50)] file:px-3 file:py-1 file:text-sm file:font-medium file:text-[var(--ink-0)]"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Expected columns: firstName, lastName, email, phone, title, source
                </p>
              </div>

              {importPreview.length > 0 && (
                <div>
                  <p className="mb-2 text-sm font-medium text-gray-700">
                    Preview ({importAllRows.length} rows total, showing first {importPreview.length})
                  </p>
                  <div className="overflow-x-auto rounded border border-gray-200">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b bg-gray-50">
                          {Object.keys(importPreview[0]).map(h => (
                            <th key={h} className="px-2 py-1.5 text-left font-medium text-gray-600">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {importPreview.map((row, i) => (
                          <tr key={i} className="border-b border-gray-100">
                            {Object.values(row).map((v, j) => (
                              <td key={j} className="px-2 py-1.5 text-gray-700">{v}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {importResult && (
                <div className={`rounded-lg px-4 py-3 text-sm ${importResult.success > 0 ? 'border border-green-200 bg-green-50 text-green-700' : 'border border-red-200 bg-red-50 text-red-700'}`}>
                  Imported {importResult.success} contacts successfully.
                  {importResult.errors > 0 && ` ${importResult.errors} rows skipped (missing firstName/lastName).`}
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <button
                onClick={() => setShowImportModal(false)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleImport}
                disabled={importing || importAllRows.length === 0}
                className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
              >
                {importing && <Loader2 size={14} className="animate-spin" />}
                Import {importAllRows.length > 0 ? `(${importAllRows.length} rows)` : ''}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
